/**
 * May this failed auth request be shown to the user, or would showing it leak
 * whether an account exists?
 *
 * ONE implementation, because two surfaces need it and two copies drift. They
 * already did: `ForgotPasswordForm` was taught the rule as a property while
 * `VerifyEmailPage` kept a hand-written `status === 429`, so the reset form
 * reported a dead API honestly and the resend button next door still said
 * "Verification email sent!" for a request that never left the browser.
 *
 * WHY THIS EXISTS AT ALL. `authFetch` RETURNS `{ error }` rather than throwing,
 * so a `try/catch` around it is dead for HTTP errors. Both surfaces discarded
 * the result and reported success for every outcome — which is #610's entire
 * signature: this flow's failure mode is silence, and it stayed broken across
 * releases because a 404 looked exactly like a sent email.
 *
 * THE RULE. Surface everything except a 5xx.
 *
 * Enumeration defence is why there is any exception. Better Auth answers 200 for
 * "account found" and "not found" alike, with a deliberate timing mitigation, so
 * anything that distinguishes them is an oracle. But that only covers what the
 * handler can emit AFTER looking the address up, and reading the dist there is
 * exactly one such class:
 *
 *   - every 4xx `/request-password-reset` can produce is thrown BEFORE
 *     `findUserByEmail` (the lone case is "Reset password isn't enabled");
 *   - the mail send itself is wrapped in `runInBackgroundOrAwait`, which catches
 *     and logs, so a mailer failure is a 200, not a 500 — an earlier version of
 *     this note said "everything after the lookup throws", which is not what the
 *     dist does and would mislead the next person widening this;
 *   - which leaves a `createVerificationValue` write failure — reached only for
 *     an address that EXISTS — as the one account-correlated response. It is a
 *     narrow window (the read path healthy, the write path broken) and it is
 *     still the whole reason for the exception.
 *
 * So a 5xx stays silent. The cost is real and accepted: during an infrastructure
 * outage that never reaches the handler at all (nginx 502/504 on a rolling
 * restart, a Cloudflare 521 — CLAUDE.md documents that exact failure on
 * origin-proxied paths) the user is told "check your email" and gets nothing.
 * The client cannot tell an edge 5xx from a handler 5xx, and guessing wrong in
 * the other direction hands out an account oracle.
 *
 * DO NOT REWRITE THIS AS A RANGE. `status >= 400 && status < 600` reads like a
 * tidy-up and silently folds the 5xx back into the surfaced set. It shipped
 * green past a source-grep guard that was written to catch precisely it; the
 * guard now is a behavioural matrix in `forgot-password-refusal.test.tsx`.
 */
export function refusalIsSafeToSurface(status: number | undefined): boolean {
  return !(status !== undefined && status >= 500)
}
